<script setup>
// Kampanya listesi: durum degistirme ve silme buradan yapilir.
//
// Durum degistirmek dogrudan para harcamasini baslatir ya da durdurur, silmek
// ise geri alinamaz. Bu yuzden her iki islem de kullanicidan acik onay ister
// ve koprude { onaylandi: true } ile gonderilir.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { aracCalistir, diziyeCevir, paraBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');
const islemdeki = ref('');
const detay = ref(null);

// Tutarlar hesabin kendi biriminde gelir; TRY varsaymak yanlis rakam gosterir.
const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

const tarihBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  return Number.isNaN(tarih.getTime()) ? String(deger) : tarih.toLocaleDateString('tr-TR');
};

// Ayrinti panelinde gun tek basina yetmiyor: bir kampanyanin gun icinde ne
// zaman durduruldugunu ancak saat gosterilince anlasiliyor.
const tarihSaatBicimle = deger => {
  if (!deger) return '—';
  const tarih = new Date(deger);
  if (Number.isNaN(tarih.getTime())) return String(deger);
  return tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

const aktifMi = satir => String(satir?.status || '').toUpperCase() === 'ACTIVE';

// Meta durumlari buyuk harfli ingilizce kod dondurur; kodu oldugu gibi basmak
// kullaniciya bir sey anlatmiyor. Eslesmeyen kod oldugu gibi kaliyor, cunku
// uydurma karsilik yanlis yonlendirir.
const DURUM_ETIKETLERI = {
  ACTIVE: 'META_ADS.ORTAK.DURUM_AKTIF',
  PAUSED: 'META_ADS.ORTAK.DURUM_DURAKLATILDI',
  DELETED: 'META_ADS.ORTAK.DURUM_SILINDI',
  ARCHIVED: 'META_ADS.ORTAK.DURUM_ARSIVLENDI',
  IN_PROCESS: 'META_ADS.ORTAK.DURUM_ISLENIYOR',
  WITH_ISSUES: 'META_ADS.ORTAK.DURUM_SORUNLU',
  PENDING_REVIEW: 'META_ADS.ORTAK.DURUM_INCELEMEDE',
  DISAPPROVED: 'META_ADS.ORTAK.DURUM_REDDEDILDI',
  PREAPPROVED: 'META_ADS.ORTAK.DURUM_ON_ONAYLI',
  PENDING_BILLING_INFO: 'META_ADS.ORTAK.DURUM_ODEME_BEKLIYOR',
  CAMPAIGN_PAUSED: 'META_ADS.ORTAK.DURUM_KAMPANYA_DURDU',
  ADSET_PAUSED: 'META_ADS.ORTAK.DURUM_SET_DURDU',
};

const durumMetni = deger => {
  const ham = String(deger || '').toUpperCase();
  if (!ham) return '—';
  const ceviri = DURUM_ETIKETLERI[ham];
  return ceviri ? t(ceviri) : ham;
};

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.ORTAK.AD') },
  { anahtar: 'status', baslik: t('META_ADS.ORTAK.DURUM'), bicim: durumMetni },
  { anahtar: 'objective', baslik: t('META_ADS.ORTAK.HEDEF') },
  {
    anahtar: 'daily_budget',
    baslik: t('META_ADS.REKLAMLAR.BUTCE'),
    // Tutarlar kurus gelir. Kampanya butcesi (CBO) gunluk ya da toplam
    // olabilir; hangisi oldugu yazilmazsa toplam butce gunluk sanilir. Butce
    // kampanya yerine reklam setinde tanimliysa iki alan da bos gelir.
    bicim: (deger, satir) => {
      if (deger) {
        return t('META_ADS.REKLAMLAR.BUTCE_GUNLUK', {
          tutar: paraBicimle(deger, birim.value),
        });
      }
      if (satir?.lifetime_budget) {
        return t('META_ADS.REKLAMLAR.BUTCE_TOPLAM', {
          tutar: paraBicimle(satir.lifetime_budget, birim.value),
        });
      }
      return '—';
    },
  },
  { anahtar: 'created_time', baslik: t('META_ADS.ORTAK.OLUSTURMA'), bicim: tarihBicimle },
]);

// Detay yaniti araca gore farkli alanlar tasir; sabit sutun yerine
// anahtar/deger listesine cevirmek her arac icin calisir. Alan adini ve ic ice
// nesneyi ham JSON olarak basmak kullaniciya hicbir sey anlatmadigi icin
// burada okunur hale getiriyoruz: ad -> Turkce etiket, tarih/para -> bicimli
// deger, ic ice nesne -> "Ust > Alt" satirlari.
const ALAN_ETIKETLERI = {
  id: 'META_ADS.ALAN.KIMLIK',
  account_id: 'META_ADS.ALAN.HESAP',
  name: 'META_ADS.ORTAK.AD',
  status: 'META_ADS.ORTAK.DURUM',
  effective_status: 'META_ADS.REKLAMLAR.ETKIN_DURUM',
  configured_status: 'META_ADS.ALAN.AYARLANAN_DURUM',
  objective: 'META_ADS.ORTAK.HEDEF',
  buying_type: 'META_ADS.ALAN.SATIN_ALMA_TURU',
  bid_strategy: 'META_ADS.ALAN.TEKLIF_STRATEJISI',
  daily_budget: 'META_ADS.ALAN.GUNLUK_BUTCE',
  lifetime_budget: 'META_ADS.ALAN.TOPLAM_BUTCE',
  budget_remaining: 'META_ADS.ALAN.KALAN_BUTCE',
  spend_cap: 'META_ADS.ALAN.HARCAMA_SINIRI',
  pacing_type: 'META_ADS.ALAN.HARCAMA_HIZI',
  special_ad_categories: 'META_ADS.ALAN.OZEL_KATEGORI',
  promoted_object: 'META_ADS.ALAN.TANITILAN',
  issues_info: 'META_ADS.ALAN.SORUNLAR',
  created_time: 'META_ADS.ORTAK.OLUSTURMA',
  updated_time: 'META_ADS.ALAN.GUNCELLEME',
  start_time: 'META_ADS.ORTAK.BASLANGIC',
  stop_time: 'META_ADS.ORTAK.BITIS',
};

// Bicimi alan adindan seciyoruz: Meta ayni adi her kaynakta ayni anlamda
// kullanir, bu yuzden ada bakmak guvenli. Para alanlari kurus gelir.
const TARIH_ALANLARI = ['created_time', 'updated_time', 'start_time', 'stop_time'];
const PARA_ALANLARI = [
  'daily_budget',
  'lifetime_budget',
  'budget_remaining',
  'spend_cap',
];
const DURUM_ALANLARI = ['status', 'effective_status', 'configured_status'];

// Iki seviyeden derini dar panelde okunmaz hale geliyor; daha derini ve uzun
// dizileri sayiyla ozetliyoruz.
const EN_FAZLA_DERINLIK = 2;
const EN_FAZLA_ACILAN_OGE = 3;

// Bilinmeyen alan adi oldugu gibi kalir; uydurma etiket yanlis bilgi verir.
const etiketle = anahtar => {
  const ceviri = ALAN_ETIKETLERI[anahtar];
  return ceviri ? t(ceviri) : anahtar;
};

const duzDeger = (anahtar, deger) => {
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.ORTAK.EVET') : t('META_ADS.ORTAK.HAYIR');
  }
  if (TARIH_ALANLARI.includes(anahtar)) return tarihSaatBicimle(deger);
  if (PARA_ALANLARI.includes(anahtar)) return paraBicimle(deger, birim.value);
  if (DURUM_ALANLARI.includes(anahtar)) return durumMetni(deger);
  return String(deger);
};

const alanlaraCevir = (nesne, atlanacak = []) => {
  const cikti = [];

  const gez = (kaynak, ustAnahtar, ustEtiket, derinlik) => {
    Object.entries(kaynak || {}).forEach(([anahtar, deger]) => {
      if (atlanacak.includes(anahtar)) return;
      // Bos alan hic gosterilmiyor: "—" satirlari dolu alanlari gozden kacirir.
      if (deger === null || deger === undefined || deger === '') return;

      const tamAnahtar = ustAnahtar ? `${ustAnahtar}.${anahtar}` : anahtar;
      const etiket = ustEtiket ? `${ustEtiket} › ${etiketle(anahtar)}` : etiketle(anahtar);

      if (Array.isArray(deger)) {
        // Bos ogeleri once atiyoruz: aksi halde listede "null" yazisi cikiyor.
        const ogeler = deger.filter(
          oge => oge !== null && oge !== undefined && oge !== ''
        );
        if (!ogeler.length) return;
        if (ogeler.every(oge => typeof oge !== 'object')) {
          cikti.push({
            anahtar: tamAnahtar,
            etiket,
            deger: ogeler.map(oge => duzDeger(anahtar, oge)).join(', '),
          });
          return;
        }
        if (derinlik < EN_FAZLA_DERINLIK && ogeler.length <= EN_FAZLA_ACILAN_OGE) {
          ogeler.forEach((oge, sira) =>
            gez(oge, `${tamAnahtar}.${sira}`, `${etiket} ${sira + 1}`, derinlik + 1)
          );
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.OGE_SAYISI', { sayi: ogeler.length }),
        });
        return;
      }

      if (typeof deger === 'object') {
        const altSayi = Object.keys(deger).length;
        if (!altSayi) return;
        if (derinlik < EN_FAZLA_DERINLIK) {
          gez(deger, tamAnahtar, etiket, derinlik + 1);
          return;
        }
        cikti.push({
          anahtar: tamAnahtar,
          etiket,
          deger: t('META_ADS.ORTAK.ALAN_SAYISI', { sayi: altSayi }),
        });
        return;
      }

      cikti.push({ anahtar: tamAnahtar, etiket, deger: duzDeger(anahtar, deger) });
    });
  };

  gez(nesne, '', '', 0);
  return cikti;
};

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_campaigns', {
      account_id: props.hesap,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const detayAc = async satir => {
  islemdeki.value = satir.id;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, 'ads_get_campaign_details', {
      campaign_id: satir.id,
    });
    const veri = Array.isArray(sonuc?.veri) ? sonuc.veri[0] : sonuc?.veri;
    detay.value = {
      ad: satir.name || satir.id,
      alanlar: alanlaraCevir(veri),
      ozet: sonuc?.ozet || '',
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

const durumDegistir = async satir => {
  const yeniDurum = aktifMi(satir) ? 'PAUSED' : 'ACTIVE';
  const okunurDurum =
    yeniDurum === 'ACTIVE'
      ? t('META_ADS.ORTAK.DURUM_AKTIF')
      : t('META_ADS.ORTAK.DURUM_DURAKLATILDI');
  const soru = t('META_ADS.REKLAMLAR.KAMPANYA_DURUM_ONAY', {
    ad: satir.name || satir.id,
    durum: okunurDurum,
  });
  if (!window.confirm(soru)) return;

  islemdeki.value = satir.id;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_update_campaign',
      { campaign_id: satir.id, status: yeniDurum },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.ORTAK.ISLEM_TAMAM'));
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

const sil = async satir => {
  const ad = satir.name || satir.id;
  // Silme Meta tarafinda geri alinamaz; kaza ile tiklamaya karsi iki asamali onay.
  if (!window.confirm(t('META_ADS.REKLAMLAR.KAMPANYA_SIL_ONAY_1', { ad }))) return;
  if (!window.confirm(t('META_ADS.REKLAMLAR.KAMPANYA_SIL_ONAY_2', { ad }))) return;

  islemdeki.value = satir.id;
  hata.value = '';
  try {
    await aracCalistir(
      route.params.accountId,
      'ads_delete_campaign',
      { campaign_id: satir.id },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.ORTAK.ISLEM_TAMAM'));
    if (detay.value?.ad === ad) detay.value = null;
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemdeki.value = '';
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <div class="flex items-center gap-3 mb-4">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('META_ADS.REKLAMLAR.KAMPANYALAR') }}
      </h2>
      <span class="grow" />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.ORTAK.YENILE')"
        @click="yukle"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>
    <p v-if="!hesap" class="text-sm text-n-slate-11">{{ t('META_ADS.ORTAK.HESAP_SECIN') }}</p>

    <div v-else class="flex items-start gap-6">
      <div class="flex-1 min-w-0">
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="sutunlar"
          :yukleniyor="yukleniyor"
          :bos-metin="t('META_ADS.REKLAMLAR.KAMPANYA_YOK')"
        >
          <template #eylemler="{ satir }">
            <div class="flex items-center justify-end gap-1">
              <Button
                variant="faded"
                size="sm"
                :label="t('META_ADS.ORTAK.DETAY')"
                :disabled="islemdeki === satir.id"
                @click="detayAc(satir)"
              />
              <Button
                variant="faded"
                size="sm"
                :icon="aktifMi(satir) ? 'i-lucide-pause' : 'i-lucide-play'"
                :label="
                  aktifMi(satir) ? t('META_ADS.ORTAK.DURDUR') : t('META_ADS.ORTAK.BASLAT')
                "
                :disabled="islemdeki === satir.id"
                @click="durumDegistir(satir)"
              />
              <button
                type="button"
                class="px-2 py-1 text-xs rounded text-n-ruby-11 hover:bg-n-ruby-3 disabled:opacity-50"
                :disabled="islemdeki === satir.id"
                @click="sil(satir)"
              >
                {{ t('META_ADS.ORTAK.SIL') }}
              </button>
            </div>
          </template>
        </AracTablosu>
      </div>

      <aside
        v-if="detay"
        class="w-80 shrink-0 p-4 border rounded-lg border-n-weak bg-n-surface-1"
      >
        <div class="flex items-start gap-2 mb-3">
          <h3 class="text-sm font-medium text-n-slate-12 break-words">{{ detay.ad }}</h3>
          <span class="grow" />
          <Button variant="faded" size="sm" icon="i-lucide-x" @click="detay = null" />
        </div>
        <p v-if="detay.ozet" class="mb-3 text-xs text-n-slate-11">{{ detay.ozet }}</p>
        <dl
          v-if="detay.alanlar.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs"
        >
          <template v-for="alan in detay.alanlar" :key="alan.anahtar">
            <dt class="break-words text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </template>
        </dl>
        <p v-else class="text-xs text-n-slate-11">
          {{ t('META_ADS.ORTAK.DETAY_YOK') }}
        </p>
      </aside>
    </div>
  </div>
</template>
